import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconCheck, IconEye, IconEyeOff, IconLock } from '@tabler/icons-react';
import { Controller, useForm } from 'react-hook-form';
import { useAuth } from '../../../features/auth';
import { pb } from '../../../shared/api/pocketbase';
import { getPbErrorMessage } from '../../../shared/lib/pbError';
import { logAction } from '../../../shared/lib/userLogger';
import { Button } from '../../../shared/ui/Button';
import { Input } from '../../../shared/ui/Input';
import { Notify, useNotify } from '../../../shared/ui/Notify/Notify';

interface PasswordForm {
  oldPassword: string;
  password: string;
  passwordConfirm: string;
}

const ResetPasswordPage: React.FC = () => {
  const { currentUser, logout, refreshUser } = useAuth();
  const { msg, notify, clear } = useNotify();
  const navigate = useNavigate();
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<PasswordForm>({
    defaultValues: { oldPassword: '', password: '', passwordConfirm: '' },
  });

  const newPassword = watch('password');

  const onSubmit = handleSubmit(async values => {
    if (!currentUser) return;
    setSaving(true);
    try {
      // Шаг 1: меняем пароль
      await pb.collection('users').update(currentUser.id, {
        oldPassword: values.oldPassword,
        password: values.password,
        passwordConfirm: values.passwordConfirm,
      });
      // Шаг 2: переавторизуемся с новым паролем — получаем свежий токен
      await pb.collection('users').authWithPassword(currentUser.email, values.password);
      // Шаг 3: сбрасываем флаг уже с новым токеном
      await pb.collection('users').update(currentUser.id, { needResetPass: false });
      await refreshUser();
      logAction('Reset password (first login)', 'EDIT');
      navigate('/kanban', { replace: true });
    } catch (err) {
      notify(getPbErrorMessage(err, 'Ошибка при смене пароля'));
    } finally {
      setSaving(false);
    }
  });

  if (!currentUser) return null;

  return (
    <div id="app-screen">
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', overflow: 'auto' }}>
      <div className="profile-card" style={{ maxWidth: 420, width: '100%' }}>
        <div className="profile-card-title">Смена пароля</div>
        <div style={{ marginBottom: 20, color: 'var(--color-text-secondary, #888)', fontSize: 13 }}>
          Для продолжения работы необходимо сменить пароль.
        </div>

        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="adm-field">
            <label className="adm-label">
              Текущий пароль <span className="adm-req">*</span>
            </label>
            <Controller
              control={control}
              name="oldPassword"
              rules={{ required: 'Введите текущий пароль' }}
              render={({ field }) => (
                <div className="adm-input-wrap">
                  <IconLock size={15} stroke={2} className="adm-input-icon" />
                  <Input
                    {...field}
                    type={showOld ? 'text' : 'password'}
                    className="adm-input adm-input-has-suffix"
                    placeholder="Текущий пароль"
                    autoComplete="current-password"
                  />
                  <span className="adm-input-suffix">
                    <button type="button" className="adm-pwd-btn" onClick={() => setShowOld(v => !v)}>
                      {showOld ? <IconEyeOff size={15} stroke={2} /> : <IconEye size={15} stroke={2} />}
                    </button>
                  </span>
                </div>
              )}
            />
            {errors.oldPassword && <div className="form-field-error">{errors.oldPassword.message}</div>}
          </div>

          <div className="adm-field">
            <label className="adm-label">
              Новый пароль <span className="adm-req">*</span>
            </label>
            <Controller
              control={control}
              name="password"
              rules={{
                required: 'Введите новый пароль',
                minLength: { value: 8, message: 'Минимум 8 символов' },
              }}
              render={({ field }) => (
                <div className="adm-input-wrap">
                  <IconLock size={15} stroke={2} className="adm-input-icon" />
                  <Input
                    {...field}
                    type={showNew ? 'text' : 'password'}
                    className="adm-input adm-input-has-suffix"
                    placeholder="Минимум 8 символов"
                    autoComplete="new-password"
                  />
                  <span className="adm-input-suffix">
                    <button type="button" className="adm-pwd-btn" onClick={() => setShowNew(v => !v)}>
                      {showNew ? <IconEyeOff size={15} stroke={2} /> : <IconEye size={15} stroke={2} />}
                    </button>
                  </span>
                </div>
              )}
            />
            {errors.password && <div className="form-field-error">{errors.password.message}</div>}
          </div>

          <div className="adm-field">
            <label className="adm-label">
              Подтвердите пароль <span className="adm-req">*</span>
            </label>
            <Controller
              control={control}
              name="passwordConfirm"
              rules={{
                required: 'Повторите новый пароль',
                validate: v => v === newPassword || 'Пароли не совпадают',
              }}
              render={({ field }) => (
                <div className="adm-input-wrap">
                  <IconLock size={15} stroke={2} className="adm-input-icon" />
                  <Input
                    {...field}
                    type={showConfirm ? 'text' : 'password'}
                    className="adm-input adm-input-has-suffix"
                    placeholder="Повторите новый пароль"
                    autoComplete="new-password"
                  />
                  <span className="adm-input-suffix">
                    <button type="button" className="adm-pwd-btn" onClick={() => setShowConfirm(v => !v)}>
                      {showConfirm ? <IconEyeOff size={15} stroke={2} /> : <IconEye size={15} stroke={2} />}
                    </button>
                  </span>
                </div>
              )}
            />
            {errors.passwordConfirm && <div className="form-field-error">{errors.passwordConfirm.message}</div>}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
            <button type="button" className="adm-pwd-btn" style={{ fontSize: 13, color: 'var(--color-text-secondary, #888)' }} onClick={logout}>
              Выйти
            </button>
            <Button variant="adm-save" type="submit" disabled={saving}>
              {saving ? <span className="adm-btn-spinner" /> : <IconCheck size={15} stroke={2.5} />}
              Сменить пароль
            </Button>
          </div>
        </form>
      </div>
      </div>
      <Notify message={msg} onDone={clear} />
    </div>
  );
};

export { ResetPasswordPage };
